/**
 * 🎨 Havlaa Logo Component
 *
 * يستخدم الصورة الفعلية للوقو (في public/logo.png).
 */

import { motion } from 'framer-motion';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  animated?: boolean;
  className?: string;
  iconOnly?: boolean;
}

// ─── Size Tokens (دقيقة + متّسقة) ───
const sizeMap = {
  sm: {
    icon: 40,
    enText: 'text-sm',
    arText: 'text-xs',
    gap: 'gap-1',
    line: 'w-4',
  },
  md: {
    icon: 64,
    enText: 'text-lg',
    arText: 'text-base',
    gap: 'gap-1.5',
    line: 'w-6',
  },
  lg: {
    icon: 96,
    enText: 'text-2xl',
    arText: 'text-xl',
    gap: 'gap-2',
    line: 'w-8',
  },
  xl: {
    icon: 128,
    enText: 'text-3xl',
    arText: 'text-2xl',
    gap: 'gap-3',
    line: 'w-10',
  },
} as const;

export function Logo({
  size = 'md',
  showText = true,
  animated = false,
  className = '',
  iconOnly = false,
}: LogoProps) {
  const tokens = sizeMap[size];

  const Wrapper = animated ? motion.div : 'div';
  const wrapperProps = animated
    ? {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.6, ease: 'easeOut' as const },
      }
    : {};

  return (
    <Wrapper
      className={`flex flex-col items-center ${className}`}
      {...wrapperProps}
    >
      {/* 🎨 الصورة الأصلية للوقو */}
      <img
        src="/logo.png"
        alt="Havlaa"
        width={tokens.icon}
        height={tokens.icon}
        className="object-contain drop-shadow-md"
        style={{ width: tokens.icon, height: tokens.icon }}
        loading="eager"
      />

      {/* النصوص (HAVLAA + هافلا) */}
      {showText && !iconOnly && (
        <div className="flex flex-col items-center mt-3 leading-tight">
          {/* English */}
          <span
            className={`${tokens.enText} font-bold tracking-[0.25em] text-brand-purple-600`}
          >
            HAVLAA
          </span>

          {/* Divider + Arabic */}
          <div className={`flex items-center ${tokens.gap} mt-1.5`}>
            <span className={`h-px ${tokens.line} bg-brand-gold-500`} />
            <span
              className={`${tokens.arText} font-display font-medium text-brand-purple-600`}
            >
              هافلا
            </span>
            <span className={`h-px ${tokens.line} bg-brand-gold-500`} />
          </div>
        </div>
      )}
    </Wrapper>
  );
}

/**
 * 🎨 Variant: Logo بدون نص — للـ navbar وأماكن صغيرة
 */
export function LogoIcon({
  size = 'md',
  animated = false,
  className = '',
}: Omit<LogoProps, 'showText' | 'iconOnly'>) {
  return (
    <Logo
      size={size}
      animated={animated}
      className={className}
      showText={false}
      iconOnly
    />
  );
}
